/**
 * What the user changed in a `product_selection` view and whether it was taken
 * over, kept outside the components so it survives chat remounts and is
 * shared by the card and its popup (same pattern as the wizard preview store;
 * entries last for the page session).
 */
import React from "react";
import type { ProductSelectionState } from "./productSelectionSpec.js";

export class ProductSelectionStore {
  private states = new Map<string, ProductSelectionState>();
  private listeners = new Map<string, Set<() => void>>();

  /** The stored state, or `initial` (the spec's own) when nothing was changed yet. */
  get(key: string, initial: ProductSelectionState): ProductSelectionState {
    return this.states.get(key) ?? initial;
  }

  update(
    key: string,
    initial: ProductSelectionState,
    change: (state: ProductSelectionState) => ProductSelectionState
  ): void {
    this.states.set(key, change(this.get(key, initial)));
    this.listeners.get(key)?.forEach(listener => listener());
  }

  subscribe(key: string, listener: () => void): () => void {
    const set = this.listeners.get(key) ?? new Set<() => void>();
    set.add(listener);
    this.listeners.set(key, set);
    return () => {
      set.delete(listener);
    };
  }

  clear(): void {
    this.states.clear();
  }
}

export const productSelectionStore = new ProductSelectionStore();

/** The view's state from the shared store; `update` writes through to it. */
export function useProductSelectionState(
  key: string,
  initial: ProductSelectionState
): [
  ProductSelectionState,
  (change: (state: ProductSelectionState) => ProductSelectionState) => void,
] {
  const subscribe = React.useCallback(
    (listener: () => void) => productSelectionStore.subscribe(key, listener),
    [key]
  );
  const state = React.useSyncExternalStore(subscribe, () =>
    productSelectionStore.get(key, initial)
  );
  const update = React.useCallback(
    (change: (current: ProductSelectionState) => ProductSelectionState) =>
      productSelectionStore.update(key, initial, change),
    [key, initial]
  );
  return [state, update];
}
